<template>
  <AppLayout>
    <div class="landing">
      <div class="headline">
        <h1 class="title">AI识别填色、在线粒径统计</h1>
        <p class="sub">
          点击下方「导入SEM图」打开导入窗口，从本地上传图片；材料结构、材料组成为选填项。
        </p>
        <div class="actions">
          <button class="btn" type="button" @click="openImportModal">导入SEM图</button>
        </div>
      </div>

      <div class="stage">
        <div class="frame">
          <img
            v-if="previewUrl"
            class="img"
            :src="previewUrl"
            alt="SEM preview"
          />
          <div v-else class="placeholder">
            <div class="ph-title">请导入一张 SEM 图片！</div>
          </div>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div v-if="importBusy" class="import-global-mask">
        <div class="import-global-inner">
          <div class="spinner"></div>
          <div class="loading-text">图片上传中，请稍候…</div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="importModalOpen" class="modal-root" @click.self="closeImportModal">
        <div class="modal" role="dialog" aria-modal="true" aria-labelledby="import-modal-title">
          <div class="modal-head">
            <div>
              <div id="import-modal-title" class="modal-title">数据导入</div>
              <div class="modal-crumb">专题领域 &gt; 小微颗粒形貌测试</div>
            </div>
            <button
              type="button"
              class="modal-close"
              aria-label="关闭"
              :disabled="importBusy"
              @click="closeImportModal"
            >
              ×
            </button>
          </div>

          <div class="modal-body">
            <p class="hint">从本机选择 SEM 图片上传（建议单张先完成流程）。</p>
            <label class="field">
              <span class="label">本地图片 <span class="req" aria-hidden="true">*</span></span>
              <input
                ref="fileInput"
                class="file-native"
                type="file"
                accept="image/*"
                @change="onFileChange"
              />
              <div class="file-line">
                <span class="file-name">{{ fileName || '未选择文件' }}</span>
                <button type="button" class="linkish" @click="$refs.fileInput.click()">选择文件</button>
              </div>
            </label>

            <label class="field">
              <span class="label">材料结构（选填）</span>
              <select v-model="materialStructure" class="input-like">
                <option value="">暂不填写</option>
                <option v-for="s in structureOptions" :key="s" :value="s">{{ s }}</option>
              </select>
            </label>
            <label class="field">
              <span class="label">材料组成（选填）</span>
              <input
                v-model.trim="materialComposition"
                class="input-like"
                type="text"
                maxlength="200"
                placeholder="选填"
                autocomplete="off"
              />
            </label>
          </div>

          <div class="modal-foot">
            <button type="button" class="btn-primary" :disabled="importBusy" @click="startImport">
              {{ importBusy ? '正在上传…' : '开始导入' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </AppLayout>
</template>

<script>
import AppLayout from '../../../layouts/AppLayout.vue';
import { uploadSemImage, createSemTask } from '../../../services/semApi';
import semExample from '../../../assets/sem_example.jpg';

const STRUCTURE_OPTIONS = ['球体', '立方体', '片状', '纳米阵列', '柱状', '不规则颗粒'];
const SEM_TASK_STORAGE_KEY = 'sem_current_task_id';

export default {
  name: 'SemLanding',
  components: { AppLayout },
  data() {
    return {
      taskId: null,
      previewUrl: semExample,
      importBusy: false,
      importModalOpen: false,
      pickedFile: null,
      fileName: '',
      materialStructure: '',
      materialComposition: '',
      structureOptions: STRUCTURE_OPTIONS,
    };
  },
  methods: {
    openImportModal() {
      this.importModalOpen = true;
    },
    closeImportModal() {
      if (this.importBusy) return;
      this.importModalOpen = false;
    },
    async onFileChange(e) {
      const f = e.target.files && e.target.files[0];
      if (!f) {
        this.pickedFile = null;
        this.fileName = '';
        return;
      }
      this.pickedFile = f;
      this.fileName = f.name;
    },
    resetFileInput() {
      this.pickedFile = null;
      this.fileName = '';
      if (this.$refs.fileInput) this.$refs.fileInput.value = '';
    },
    async startImport() {
      if (!this.pickedFile) {
        window.alert('请选择本地图片文件');
        return;
      }

      this.importBusy = true;
      try {
        const res = await uploadSemImage(this.pickedFile);
        this.taskId = res.taskId;
        this.previewUrl = res.previewUrl;

        await createSemTask({
          taskId: this.taskId,
          clientId: 'anonymous',
          imgUrl: this.previewUrl,
          materialStructure: this.materialStructure,
          materialComposition: this.materialComposition,
        });
        sessionStorage.setItem(SEM_TASK_STORAGE_KEY, String(this.taskId));

        this.importModalOpen = false;
        this.resetFileInput();

        this.$router.push({
          name: 'semStep1',
          query: { taskId: this.taskId },
        });
      } catch (err) {
        console.error(err);
        window.alert(err?.message || '上传失败，请重试');
      } finally {
        this.importBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.landing {
  padding: 4px 6px 0;
}

.headline {
  text-align: center;
  margin-top: 14px;
}

.title {
  margin: 0;
  font-size: 26px;
  letter-spacing: 0.5px;
  color: #111827;
}

.sub {
  margin: 10px auto 0;
  max-width: 760px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
}

.actions {
  margin-top: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 10px;
  background: #5fa3e6;
  color: #ecfeff;
  font-size: 12px;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(59, 130, 246, 0.25);
  border: none;
}

.btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.stage {
  display: flex;
  justify-content: center;
  margin-top: 18px;
}

.frame {
  position: relative;
  width: min(820px, calc(100% - 24px));
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.28);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.import-global-mask {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.import-global-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #e5e7eb;
  font-size: 12px;
}

.spinner {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(226, 232, 240, 0.5);
  border-top-color: #3b82f6;
  animation: spin 0.8s linear infinite;
  margin-bottom: 8px;
}

.loading-text {
  letter-spacing: 0.5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder {
  text-align: center;
  color: #6b7280;
}

.ph-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

/* Modal */
.modal-root {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.modal {
  width: min(520px, 100%);
  max-height: min(90vh, 720px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
  border: 1px solid rgba(148, 163, 184, 0.25);
}

.modal-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px 10px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
}

.modal-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
}

.modal-crumb {
  margin-top: 4px;
  font-size: 11px;
  color: #94a3b8;
}

.modal-close {
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  padding: 0 4px;
}

.modal-close:hover {
  color: #0f172a;
}

.modal-body {
  padding: 14px 18px 8px;
}

.hint {
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.55;
  color: #475569;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.field .label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.req {
  color: #dc2626;
  font-weight: 700;
  margin-left: 2px;
}

.modal-close:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.input-like {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  font-size: 12px;
  background: #fff;
}

.input-like:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
}

.file-native {
  display: none;
}

.file-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px dashed rgba(148, 163, 184, 0.55);
  background: rgba(248, 250, 252, 0.9);
}

.file-name {
  font-size: 12px;
  color: #475569;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.linkish {
  flex-shrink: 0;
  border: none;
  background: none;
  color: #3b82f6;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

.modal-foot {
  padding: 12px 18px 18px;
  display: flex;
  justify-content: center;
}

.btn-primary {
  min-width: 200px;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: #5fa3e6;
  color: #ecfeff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 22px rgba(59, 130, 246, 0.22);
}

.btn-primary:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
